import { arrayOf, shape, string } from 'prop-types';
import React from 'react';
import { GoPencil } from 'react-icons/go';

import TimeStamp from '../generic/TimeStamp';
import BookingEntryChange from './changes/BookingEntryChange';
import CampaignLineChange from './changes/CampaignLineChange';
import OwnerChange from './changes/OwnerChange';

function changeComponent(field) {
  const component = ({ valueBefore, valueAfter }) => (
    <div>
      <b>{field}</b>
      {valueBefore
        ? ` changed from "${valueBefore}" to "${valueAfter}".`
        : ` changed to "${valueAfter}".`}
    </div>
  );

  component.propTypes = {
    valueAfter: string.isRequired,
    valueBefore: string,
  };
  component.defaultProps = {
    valueBefore: '',
  };

  component.displayName = `changeComponent(${field})`;

  return component;
}

const LISTED_CHANGES = [
  { field: 'owner', Component: OwnerChange },
  { field: 'status', Component: changeComponent('Status') },
  { field: 'stage', Component: changeComponent('Stage') },
  { field: 'expectedRevenue', Component: changeComponent('Budget') }, // opportunity
  { field: 'netClientBudget', Component: changeComponent('Net Client Budget') }, // quote
  { field: 'campaignStart', Component: changeComponent('Campaign start') }, // opportunity, quote
  { field: 'campaignEnd', Component: changeComponent('Campaign end') }, // opportunity, quote
  // campaign
  { field: 'startDatePlan', Component: changeComponent('Planned start date') },
  { field: 'endDatePlan', Component: changeComponent('Planned end date') },
  { field: 'startDateIs', Component: changeComponent('Actual start date') },
  { field: 'endDateIs', Component: changeComponent('Actual end date') },
  {
    field: 'billableBudgetMediaInternal',
    Component: changeComponent('Billable Budget Media Internal'),
  },
  {
    field: 'spendableBudget',
    Component: changeComponent('Spendable Budget'),
  },
  {
    field: 'freeService',
    Component: changeComponent('Free Service'),
  },
  // Invoice
  { field: 'purchaseOrderNumber', Component: changeComponent('PO Number') },
  { field: 'account', Component: changeComponent('Billing Account') },
  { field: 'debitAmount', Component: changeComponent('Debit Amount') },
  { field: 'discount', Component: changeComponent('Discount') },
  {
    field: /^bookingEntries\.\d+\.\w+$/,
    Component: BookingEntryChange,
  },
  // Campaign Lines
  {
    field: /^campaignLine\.CL-\d+\.status$/,
    Component: CampaignLineChange,
  },
  {
    field: /^campaignLine\.CL-\d+\.freeService$/,
    Component: CampaignLineChange,
  },
  {
    field: /^campaignLine\.CL-\d+\.netClientBudget$/,
    Component: CampaignLineChange,
  },
  {
    field: /^campaignLine\.CL-\d+\.coInvest$/,
    Component: CampaignLineChange,
  },
  {
    field: /^campaignLine\.CL-\d+\.wkzBudget$/,
    Component: CampaignLineChange,
  },
  // ZMSAM Merchant
  { field: 'framework', Component: changeComponent('Merchant Framework') },
  { field: 'pc', Component: changeComponent('Partner Consultant') },
];

function LogEntry({ changedBy, timestamp, changes }) {
  return (
    <>
      <div className="d-flex w-100 justify-content-between small">
        <span>
          <GoPencil /> {changedBy}
        </span>
        <TimeStamp>{timestamp}</TimeStamp>
      </div>
      {LISTED_CHANGES.map(({ field, Component }) => {
        return {
          field,
          Component,
          change: changes.find(({ fieldChanged }) =>
            field instanceof RegExp
              ? field.test(fieldChanged)
              : fieldChanged === field,
          ),
        };
      })
        .filter(({ change }) => !!change)
        .map(({ Component, change, field }) => (
          <Component key={field} {...change} />
        ))}
    </>
  );
}

LogEntry.propTypes = {
  timestamp: string.isRequired,
  changedBy: string.isRequired,

  changes: arrayOf(
    shape({
      fieldChanged: string.isRequired,
      valueBefore: string,
      valueAfter: string,
    }),
  ).isRequired,
};

export default LogEntry;
